<template>
  <v-dialog v-if="show" v-model="show" persistent width="75%">
    <v-card color="cardbg">
      <v-card-text class="headline font-weight-bold black--text pt-5">
        {{ $t(dialogTitle) }}
      </v-card-text>
      <v-card-text>
        <v-form class="form-container" ref="machineryForm" v-model="formValid">
          <v-row no-gutters>
            <!-- machinery name -->
            <v-col class="col-12">
              <v-text-field
                v-model="machineryName"
                class="form-input"
                :placeholder="
                  $t('hfManagement.machineryPerformance.form.insertName')
                "
                :rules="[rules.required]"
                :disabled="!hasWritePerm"
                required
                regular
              >
                <span slot="prepend" class="title mt-1">{{
                  $t("hfManagement.machineryPerformance.form.name")
                }}</span>
              </v-text-field>
            </v-col>
            <!-- machinery code -->
            <v-col class="col-12">
              <v-text-field
                v-model="machineryCode"
                class="form-input"
                :placeholder="
                  $t('hfManagement.machineryPerformance.form.insertCode')
                "
                :rules="[rules.required]"
                :disabled="!hasWritePerm"
                required
                regular
              >
                <span slot="prepend" class="title mt-1">{{
                  $t("hfManagement.machineryPerformance.form.code")
                }}</span>
              </v-text-field>
            </v-col>
            <!-- machinery services -->
            <v-col class="col-12">
              <v-autocomplete
                v-model="machineryServiceList"
                :items="fullServiceList"
                :placeholder="
                  $t('hfManagement.machineryPerformance.form.findServices')
                "
                return-object
                item-text="nameByLang"
                class="input-multi-select"
                multiple
                chips
                deletable-chips
                :hide-selected="true"
                auto-select-first
                :menu-props="{
                  closeOnContentClick: true,
                  maxWidth: 'min-content'
                }"
                :disabled="!hasWritePerm"
              >
                <span slot="prepend" class="title mt-1 black--text">{{
                  $t("hfManagement.machineryPerformance.form.services")
                }}</span>
                <template v-slot:selection="data">
                  <v-tooltip class="service-item" color="primary" bottom>
                    <template v-slot:activator="{ on }">
                      <v-chip
                        v-on="on"
                        :input-value="data.selected"
                        class="service-chip text-uppercase white--text my-1 mr-1"
                        small
                        color="primary"
                        :disabled="!hasWritePerm"
                        :close="hasWritePerm"
                        @click:close="removeMachineryServiceItem(data.item)"
                        ><span class="font-weight-bold text-truncate">
                          {{ data.item.nameByLang }}
                        </span></v-chip
                      >
                    </template>
                    <span class="font-weight-bold text-uppercase caption">
                      {{ data.item.nameByLang }}
                    </span>
                  </v-tooltip>
                </template>
                <template v-slot:item="data">
                  <v-list-item-content class="text-uppercase">{{
                    data.item.nameByLang
                  }}</v-list-item-content>
                </template>
                <template v-slot:no-data>
                  <base-table-no-data
                    message="hfManagement.machineryPerformance.form.noServices"
                  ></base-table-no-data>
                </template>
              </v-autocomplete>
            </v-col>
            <!-- machinery constraints -->
            <v-col class="col-12">
              <v-row class="title" no-gutters>
                <v-item-group
                  mandatory
                  v-model="selectedConstraintTypes"
                  multiple
                >
                  <v-row no-gutters>
                    <v-col
                      v-for="(item, index) in constraintsTypes"
                      :key="index"
                      class="col-auto mr-3"
                    >
                      <v-item>
                        <v-checkbox
                          :label="item.label"
                          class="checkbox-item"
                          :disabled="!hasWritePerm"
                          :value="item.id"
                          v-model="selectedConstraintTypes"
                          hide-details
                        ></v-checkbox>
                      </v-item>
                    </v-col>
                  </v-row>
                  <div
                    v-if="
                      isConstraintTypeFormTouched && !isConstraintTypeDefined
                    "
                    class="v-messages theme--light error--text"
                    role="alert"
                  >
                    <div class="v-messages__wrapper">
                      <div class="v-messages__message custom-error-msg">
                        {{
                          $t(
                            "hfManagement.machineryPerformance.form.noConstraintSelectedErrorMsg"
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </v-item-group>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-row class="no-gutters px-3">
          <v-col v-if="isEditMode && hasWritePerm" class="col-auto">
            <v-btn
              class="caption font-weight-bold ma-1"
              color="primary"
              rounded
              depressed
              @click="deleteMachinery"
              >{{ $t("deleteButton") }}</v-btn
            >
          </v-col>
          <v-col class="col-auto ml-md-auto">
            <v-btn
              class="caption font-weight-bold ma-1"
              color="primary"
              outlined
              rounded
              depressed
              @click="closeMachineryFormDialog"
              >{{ $t("backButton") }}</v-btn
            >
          </v-col>
          <v-col v-if="hasWritePerm" class="col-auto">
            <v-btn
              class="caption font-weight-bold ma-1"
              color="primary"
              rounded
              depressed
              @click="saveMachinery"
              :disabled="!isFormValid"
              >{{ getSaveBtnLabel }}</v-btn
            >
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ValidationRules from "../../views/validationRules";

let vrules = new ValidationRules();

export default {
  props: {
    hasWritePerm: {
      type: Boolean,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    dialogTitle: {
      type: String,
      required: false
    },
    machinery: {
      type: Object,
      required: true
    },
    officeId: {
      type: String,
      required: true
    },
    initFormValid: {
      type: Boolean,
      required: false,
      default: true
    },
    isEditMode: {
      type: Boolean,
      required: false
    }
  },
  watch: {
    visible() {
      if (this.visible) {
        this.isConstraintTypeFormTouched = false;
        let claustrophobia = this.$store.getters["machinery/claustrophobia"];
        let obesity = this.$store.getters["machinery/obesity"];
        this.selectedConstraintTypes = [];
        if (claustrophobia) this.selectedConstraintTypes.push("claustrophobia");
        if (obesity) this.selectedConstraintTypes.push("obesity");
      }
    },
    selectedConstraintTypes: {
      handler(val) {
        let CLAUSTROPHOBIA_SELECTED = val.includes("claustrophobia");
        let OBESITY_SELECTED = val.includes("obesity");
        this.$store.commit(
          "machinery/SET_MACHINERY_FORM_CLAUSTROPHOBIA",
          CLAUSTROPHOBIA_SELECTED
        );
        this.$store.commit(
          "machinery/SET_MACHINERY_FORM_OBESITY",
          OBESITY_SELECTED
        );
        if (!this.isConstraintTypeFormTouched) {
          // if user has selected at least one constraint it means that the form (only the checkboxes) is dirty
          this.isConstraintTypeFormTouched =
            CLAUSTROPHOBIA_SELECTED || OBESITY_SELECTED;
        }
      }
    }
  },
  data() {
    return {
      isConstraintTypeFormTouched: false,
      selectedConstraintTypes: [],
      formValid: this.initFormValid,
      loadingServicesMsg: `${this.$t("hfManagement.noServiceMsg")}`,
      rules: vrules,
      checkboxErrorActive: false,
      constraintsTypes: [
        {
          id: "claustrophobia",
          label: this.$t(
            "hfManagement.machineryPerformance.form.byClaustrophobia"
          )
        },
        {
          id: "obesity",
          label: this.$t("hfManagement.machineryPerformance.form.byObesity")
        }
      ]
    };
  },
  created() {},
  methods: {
    toggle(val) {
      this.isConstraintTypeFormTouched = true;
      if (this.selectedConstraintTypes.includes(val)) {
        this.selectedConstraintTypes.push(val);
      } else {
        let index = this.selectedConstraintTypes.indexOf(val);
        if (index !== -1) this.selectedConstraintTypes.splice(index, 1);
        this.selectedConstraintTypes.splice(val);
      }
    },
    closeMachineryFormDialog() {
      this.isConstraintTypeFormTouched = false;
      this.$refs.machineryForm.resetValidation();
      this.$emit("close");
      this.show = false;
    },
    saveMachinery() {
      if (this.isFormValid) {
        this.$emit("savemachinery");
      }
    },
    removeMachineryServiceItem(machineryServiceItem) {
      this.$store.commit(
        "machinery/REMOVE_MACHINERY_FORM_SERVICE_ITEM",
        machineryServiceItem
      );
    },
    deleteMachinery() {
      let deleteConfig = {
        title: "hfManagement.machineryPerformance.deleteDialog.title",
        message: "hfManagement.machineryPerformance.deleteDialog.msg",
        params: { msg: { machineryName: this.machineryName } },
        cancelBtnLabel: "cancelButton",
        confirmBtnLabel: "deleteButton",
        confirmBtnClb: this.deletePermanently
      };
      this.$store.commit("confirm/openConfirmDialog", deleteConfig, {
        root: true
      });
    },
    deletePermanently() {
      this.$emit("deletemachinery");
    }
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      }
    },
    isConstraintTypeDefined() {
      return this.selectedConstraintTypes.some(sct =>
        this.constraintsTypes.find(i => i.id == sct)
      );
    },
    getSaveBtnLabel() {
      return this.isEditMode
        ? this.$i18n.t("saveButton")
        : this.$i18n.t("add.default");
    },
    isFormValid() {
      return this.formValid && this.isConstraintTypeDefined;
    },
    machineryName: {
      get() {
        return this.$store.getters["machinery/machineryName"];
      },
      set(machineryName) {
        this.$store.commit("machinery/SET_MACHINERY_FORM_NAME", machineryName);
      }
    },
    machineryCode: {
      get() {
        return this.$store.getters["machinery/machineryCode"];
      },
      set(machineryCode) {
        this.$store.commit("machinery/SET_MACHINERY_FORM_CODE", machineryCode);
      }
    },
    machineryServiceList: {
      get() {
        return this.$store.getters["machinery/machineryServiceList"];
      },
      set(machineryServiceList) {
        this.$store.commit(
          "machinery/SET_MACHINERY_FORM_SERVICE_LIST",
          machineryServiceList
        );
      }
    },
    claustrophobia: {
      get() {
        return this.$store.getters["machinery/claustrophobia"];
      },
      set(claustrophobia) {
        this.$store.commit(
          "machinery/SET_MACHINERY_FORM_CLAUSTROPHOBIA",
          claustrophobia
        );
      }
    },
    obesity: {
      get() {
        return this.$store.getters["machinery/obesity"];
      },
      set(obesity) {
        this.$store.commit("machinery/SET_MACHINERY_FORM_OBESITY", obesity);
      }
    },
    fullServiceList() {
      return this.$store.getters["machinery/serviceList"];
    },
    initialMachineryServiceList() {
      return this.$store.getters["machinery/initialMachineryServiceList"];
    }
  }
};
</script>

<style scoped>
.custom-error-msg {
  font-weight: 400 !important;
}
.form-input >>> .v-input__prepend-outer {
  width: 110px !important;
}
.input-multi-select >>> .v-input__prepend-outer {
  width: 110px !important;
}
.input-multi-select >>> .service-chip .v-chip__content {
  max-width: 380px;
}
.checkbox-item >>> .v-label {
  font-size: 20px;
  color: black;
}
</style>
